import {
  getApplicationForLoanExistsErrorEvent,
  getOtherNonTechnicalErrorEvent,
} from '@sec-loans-ui/common/steps/assistance-step/components/ifp-loans-dropdown/modals'
import { SecLoansAnalyticsService } from '@sec-loans-ui/services/analyticsService'
import { useAnalyticsService } from '@sec-loans-ui/state/analyticsContext'
import { IfpCreateApplicationRequest, SecLoansErrorCode } from '@shared/sec-loans'
import { usePersistingActor } from '@ui-common/hooks'
import { GlobalServicesContext } from '@ui-common/state'
import { isErrorResponse } from '@ui-common/utils'
import { getFailureModalOptions } from '@ui-common/utils/modalUtils'
import { useSelector } from '@xstate/react'
import { use } from 'react'
import { useIntl } from 'react-intl'
import { ContextFrom, StateValueFrom } from 'xstate'

import { createNewIfpApplicationMachine, NewIfpApplicationMachineType } from './newIfpApplicationMachine'

interface UseNewIfpApplicationMachineLogicReturn {
  submit: (data: IfpCreateApplicationRequest) => void
  stateValue: StateValueFrom<NewIfpApplicationMachineType>
  createdApplicationId: ContextFrom<NewIfpApplicationMachineType>['response']
  openErrorModal: () => void
  analyticsService: SecLoansAnalyticsService
}

export const useNewIfpApplicationMachineLogic = ({
  onGoToApplicationsClick,
}: {
  onGoToApplicationsClick: () => void
}): UseNewIfpApplicationMachineLogicReturn => {
  const intl = useIntl()
  const machine = createNewIfpApplicationMachine()
  const actor = usePersistingActor<NewIfpApplicationMachineType>(machine)
  const { analyticsService } = useAnalyticsService()
  const { openModal } = use(GlobalServicesContext)

  const {
    value,
    context: { response, error },
  } = useSelector(actor, (s) => s)

  const onPromptConfirm = () => {
    actor.send({ type: 'PROMPT_CONFIRMED' })
    analyticsService.sendModalCustomEvent('ErrorPromptModal', 'ConfirmButton')
  }

  const openErrorModal = () => {
    const getErrorModal = () => {
      if (isErrorResponse(error)) {
        switch (error.errorCode) {
          case SecLoansErrorCode.ApplicationAlreadyExists:
            return getApplicationForLoanExistsErrorEvent({
              onGoToApplications: () => {
                actor.send({ type: 'PROMPT_CONFIRMED' })
                analyticsService.sendModalCustomEvent('ErrorPromptModal', 'GoToApplicationsButton')
                onGoToApplicationsClick()
              },
              onPromptConfirm,
              formatMessage: intl.formatMessage,
              requestId: error.requestId,
            })
          case SecLoansErrorCode.OtherNonTechnical:
            return getOtherNonTechnicalErrorEvent({
              onPromptConfirm,
              formatMessage: intl.formatMessage,
              requestId: error.requestId,
            })
          default:
            return getFailureModalOptions(intl.formatMessage, onPromptConfirm, error.requestId)
        }
      }
      return getFailureModalOptions(intl.formatMessage, onPromptConfirm)
    }

    analyticsService.sendModalViewEvent('ErrorPromptModal')
    openModal(getErrorModal())
  }

  return {
    submit: (data: IfpCreateApplicationRequest) => {
      actor.send({ type: 'SUBMIT', data })
    },
    stateValue: value,
    createdApplicationId: response,
    openErrorModal,
    analyticsService,
  }
}
